import React from "react";
import { IDSInput, IDSCheckbox, IDSRadio, IDSAlert } from '@inera/ids-react';
import LinkifiedText, { buildLinkifiedNodes } from '../common/LinkifiedText';


function QuestionItem({ question, index, value, answers = {}, onAnswerChange, parentOptionText = null, validationErrors = new Set() }) {
  const {
    questionId,
    questionText,
    questionType,
    required,
    questionDescription,
    allowsMultiple,
  } = question;

  const options = Array.isArray(question.options) ? question.options : [];

  const currentValue = allowsMultiple
    ? Array.isArray(value)
      ? value
      : value
      ? [value]
      : []
    : value || "";

  const hasError = validationErrors.has(questionId);

  const handleChangeSingle = (e) => {
    onAnswerChange(questionId, Number(e.target.value));
  };

  const handleChangeMulti = (e, optionId) => {
    const checked = e.target.checked;

    let next;
    if (checked) {
      next = currentValue.includes(optionId)
        ? currentValue
        : [...currentValue, optionId];
    } else {
      next = currentValue.filter((v) => v !== optionId);
    }

    onAnswerChange(questionId, next);
  };

  const renderInput = () => {
    switch (questionType) {
      case "TEXT":
        return (
          <IDSInput
            type="text"
            name={`q_${questionId}`}
            value={currentValue}
            onChange={(e) => onAnswerChange(questionId, e.target.value)}
            required={required}
          />
        );

      case "MULTIPLE_CHOICE":
        if (options.length === 0) {
          return <p className="ids-mt-4">Inga svarsalternativ definierade.</p>;
        }
        if (allowsMultiple) {
          return (
            <div
              className="ids-flex"
              style={options.length > 4
                ? { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }
                : { flexDirection: 'column', gap: '1rem' }}
            >
              {options.map((opt) => (
                <IDSCheckbox
                  key={`${opt.optionId || opt.optionText}-${currentValue.includes(opt.optionId)}`}
                  defaultChecked={currentValue.includes(opt.optionId)}
                  onChange={(e) => handleChangeMulti(e, opt.optionId)}
                >
                  {opt.optionText}
                </IDSCheckbox>
              ))}
            </div>
          );
        }
        return (
          <div className="ids-flex" style={{ gap: '2rem' }}>
            {options.map((opt) => (
              <IDSRadio
                key={opt.optionId || opt.optionText}
                name={`q_${questionId}`}
                value={opt.optionId}
                checked={currentValue === opt.optionId}
                onChange={handleChangeSingle}
                required={required}
              >
                {opt.optionText}
              </IDSRadio>
            ))}
          </div>
        );

      default:
        return (
          <IDSInput
            type="text"
            name={`q_${questionId}`}
            value={currentValue}
            onChange={(e) => onAnswerChange(questionId, e.target.value)}
            required={required}
          />
        );
    }
  };

  const errorMessage =
    questionType === "MULTIPLE_CHOICE"
      ? allowsMultiple
        ? "Du måste kryssa i minst ett alternativ."
        : "Du måste välja ett alternativ."
      : "Du måste besvara frågan.";

  let followUps = [];

  if (questionType === "MULTIPLE_CHOICE" && options && options.length > 0) {
    if (allowsMultiple) {
      followUps = options
        .filter(
          (opt) =>
            currentValue.includes(opt.optionId) &&
            opt.followUpQuestion
        )
        .map((opt) => ({
          question: opt.followUpQuestion,
          parentText: opt.optionText
        }));
    } else {
      const selectedOpt = options.find(
        (opt) => opt.optionId === currentValue
      );
      if (selectedOpt && selectedOpt.followUpQuestion) {
        followUps = [{
          question: selectedOpt.followUpQuestion,
          parentText: selectedOpt.optionText
        }];
      }
    }
  }

  // Display question text with parent option prefix if it's a follow-up
  const displayText = parentOptionText
    ? `${parentOptionText} - ${questionText}`
    : questionText;

  return (
    <div className="ids-mb-5">
      <p style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: questionDescription ? '0.2rem' : '0.5rem', overflowWrap: 'anywhere' }}>
        {index}. {buildLinkifiedNodes(displayText)}
        {required && (
          <span style={{ color: 'var(--ids-palette-error-40, #c00)' }} title="Obligatorisk"> *</span>
        )}
      </p>

      <LinkifiedText
        className="ids-mb-2"
        style={{ color: 'var(--ids-palette-neutral-30)', fontSize: '0.875rem', whiteSpace: 'pre-line' }}
      >
        {questionDescription}
      </LinkifiedText>

      <div className="ids-mb-2">{renderInput()}</div>

      {hasError && (
        <div data-question-error="true" style={{ marginTop: '0.5rem' }}>
          <IDSAlert ribbon type="error">
            {errorMessage}
          </IDSAlert>
        </div>
      )}

      {followUps.length > 0 && (
        <div className="ids-mt-4">
          {followUps.map((fu, idx) =>
            fu.question && fu.question.questionId ? (
              <QuestionItem
                key={fu.question.questionId}
                question={fu.question}
                index={`${index}.${idx + 1}`}
                value={answers[fu.question.questionId] ?? ""}
                answers={answers}
                onAnswerChange={onAnswerChange}
                parentOptionText={fu.parentText}
                validationErrors={validationErrors}
              />
            ) : null
          )}
        </div>
      )}
    </div>
  );
}

export default QuestionItem;
